const parseDateOnly = (value) => {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const formatDateOnly = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

/** User-facing date label (dd-mm-yyyy) from ISO yyyy-mm-dd. */
const formatDateDisplay = (isoDate) => {
    const match = String(isoDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return isoDate || '';
    return `${match[3]}-${match[2]}-${match[1]}`;
};

/** Inclusive trip days: Day 1 = departure, Day N = return (matches itinerary day count). */
export const computeReturnDateFromDuration = (departureDate, durationDays) => {
    if (!departureDate || durationDays == null || durationDays === '') return '';
    const dep = parseDateOnly(departureDate);
    const days = parseInt(durationDays, 10);
    if (!dep || Number.isNaN(dep.getTime()) || !Number.isFinite(days) || days <= 0) return '';

    const ret = new Date(dep.getTime());
    ret.setDate(ret.getDate() + days - 1);
    return formatDateOnly(ret);
};

export const countInclusiveTripDays = (departureDate, returnDate) => {
    const dep = parseDateOnly(departureDate);
    const ret = parseDateOnly(returnDate);
    if (!dep || !ret || Number.isNaN(dep.getTime()) || Number.isNaN(ret.getTime())) return null;

    const diffMs = ret.getTime() - dep.getTime();
    if (diffMs < 0) return null;
    return Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
};

export const returnDateMatchesDuration = (departureDate, returnDate, durationDays) => {
    const expectedDays = parseInt(durationDays, 10);
    if (!Number.isFinite(expectedDays) || expectedDays <= 0) return true;
    if (!departureDate || !returnDate) return true;
    return countInclusiveTripDays(departureDate, returnDate) === expectedDays;
};

export const applyReturnDatesToDepartures = (formData, durationDays) => {
    const days = parseInt(durationDays, 10);
    if (!Number.isFinite(days) || days <= 0) return formData;

    const departures = formData.departures?.departures;
    if (!Array.isArray(departures) || departures.length === 0) return formData;

    return {
        ...formData,
        departures: {
            ...(formData.departures || {}),
            departures: departures.map((item) => (
                item?.departure_date
                    ? {
                        ...item,
                        return_date: computeReturnDateFromDuration(item.departure_date, days),
                    }
                    : item
            )),
        },
    };
};

/**
 * When departure changes, always recompute return.
 * When return is empty or invalid for the package duration, fill it in.
 */
export const syncDeparturesReturnDates = (prevDepartures, nextDepartures, durationDays) => {
    const days = parseInt(durationDays, 10);
    if (!Array.isArray(nextDepartures) || !Number.isFinite(days) || days <= 0) {
        return nextDepartures;
    }

    const prevItems = prevDepartures || [];
    return nextDepartures.map((item, index) => {
        if (!item?.departure_date) return item;

        const prevItem = prevItems[index];
        const departureChanged = item.departure_date !== prevItem?.departure_date;
        const returnChanged = item.return_date !== prevItem?.return_date;
        const expectedReturn = computeReturnDateFromDuration(item.departure_date, days);
        const returnInvalid = !returnDateMatchesDuration(
            item.departure_date,
            item.return_date,
            days
        );

        if (departureChanged || !item.return_date) {
            return { ...item, return_date: expectedReturn };
        }

        // User edited return manually — keep their value; validation runs separately.
        if (returnChanged) {
            return item;
        }

        if (returnInvalid) {
            return { ...item, return_date: expectedReturn };
        }

        return item;
    });
};

export const findDepartureDurationErrorsForForm = (departuresData, packageDuration) => {
    const expectedDays = parseInt(packageDuration, 10);
    if (!Number.isFinite(expectedDays) || expectedDays <= 0) return [];

    const errors = [];
    (departuresData?.departures || []).forEach((item, index) => {
        const { departure_date: departureDate, return_date: returnDate } = item || {};
        if (!departureDate || !returnDate) return;

        if (returnDateMatchesDuration(departureDate, returnDate, expectedDays)) return;

        const suggestedReturn = computeReturnDateFromDuration(departureDate, expectedDays);
        errors.push({
            key: `departures.${index}.return_date`,
            message: suggestedReturn
                ? `Return date must match the ${expectedDays}-day package (expected ${formatDateDisplay(suggestedReturn)}).`
                : `Return date must match the ${expectedDays}-day package duration.`,
        });
    });

    return errors;
};

// Earliest departure first. Rows with no/invalid date (e.g. a freshly added blank row) go last.
export const sortDeparturesByDate = (list = []) =>
    [...list].sort((a, b) => {
        const ta = a?.departure_date ? new Date(a.departure_date).getTime() : NaN;
        const tb = b?.departure_date ? new Date(b.departure_date).getTime() : NaN;
        if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
        if (Number.isNaN(ta)) return 1;
        if (Number.isNaN(tb)) return -1;
        return ta - tb;
    });

